/**
 * importTierOptions — which tiers the bulk importer's reference list may offer
 * on a given row, and how the row's current pick is labelled.
 *
 * Extracted from BulkImport rather than written inline because BulkImport is a
 * ~5.9k-line component with no test harness, and this is the lookup that
 * decides whether a per-reference tier selector shows the right calibre's tiers
 * or some other calibre's.
 *
 * The index is built from the calibre-check step's own output map, NOT from
 * anything stamped on the entries. `stampCalibreOnEntry` only copies
 * `_calibreData` onto an entry when the legacy-movement dual-write flag is on,
 * so an entry is not a reliable place to read tiers from — but every entry that
 * links a calibre carries `_calibreId`, and the check step's map holds the full
 * calibre for each. One index, keyed by id, serves every row.
 */
import { tiersForBrand } from './calibreTiers';
import type { CalibreMapEntry } from './ImportCalibreCheck';
import type { CalibreTier } from '../../services/firebase';

/**
 * `custom_calibres/{id}` → the tiers the importing brand may assign on it.
 *
 * Entries with no id are skipped: a row that resolved to a calibre the check
 * step could not name a document for imports a string and links nothing, so
 * there is no tier to offer against it. Calibres with no brand-visible tiers are
 * skipped too, so a present-but-empty entry never has to be told apart from an
 * absent one — `tierOptionsFor` returns `[]` either way.
 */
export function buildImportTierIndex(
  calibreMap: Map<string, CalibreMapEntry>,
  brandId: string | undefined,
): Map<string, CalibreTier[]> {
  const index = new Map<string, CalibreTier[]>();
  for (const entry of calibreMap.values()) {
    // The matched calibre, plus every revision in its line: the resolver (or
    // the curator on the review grid) may point a row at any of them.
    const candidates = [{ id: entry.id, data: entry.data }, ...(entry.revisionLine ?? [])];
    for (const c of candidates) {
      if (!c.id || index.has(c.id)) continue;
      const tiers = tiersForBrand(c.data?.tiers, brandId);
      if (tiers.length > 0) index.set(c.id, tiers);
    }
  }
  return index;
}

/**
 * The tiers offerable on one import row.
 *
 * Empty — meaning "render no selector at all" — for a row that linked no
 * calibre, and for one whose calibre has no tiers this brand may use. That
 * second case is the common one: without it every ETA and Miyota row in a
 * 600-reference import would grow a dropdown with nothing in it but
 * "— No tier —".
 */
export function tierOptionsFor(
  index: Map<string, CalibreTier[]>,
  calibreId: unknown,
): CalibreTier[] {
  return typeof calibreId === 'string' ? index.get(calibreId) ?? [] : [];
}

/**
 * How a row's tier should read: the tier's name, plus whether the admin chose it.
 *
 * `'auto'` is anything the pipeline decided on its own — the base+tier split in
 * the check step, or `detectTierFromCalibreString` at stamp time. `'set'` marks
 * a row this admin has touched here, so a scan down the column separates the
 * two without opening anything. Returns `null` when the row carries no tier,
 * which the selector shows as "— No tier —" with no suffix.
 */
export function tierRowLabel(
  tierName: unknown,
  manuallySet: boolean,
): { name: string; origin: 'auto' | 'set' } | null {
  if (typeof tierName !== 'string' || !tierName.trim()) return null;
  return { name: tierName.trim(), origin: manuallySet ? 'set' : 'auto' };
}
